// SubscriptionPackagesVersion2 only. Shared table methods are in tableMixin.js.
// Methods here with the same name as a tableMixin method replace it on this page.
import axios from "axios";

export default {
    name: "SubscriptionPackagesVersion2",
    methods: {
        /* ---------------- Permission picker: tick boxes ---------------- */
        isAvailablePermissionCategorySelected(permissions) {
            return permissions.length > 0 &&
                permissions.every((permission) => !!this.selectedAvailablePermissions[permission.id]);
        },
        toggleAvailablePermissionCategorySelection(permissions) {
            const allSelected = permissions.every((permission) => !!this.selectedAvailablePermissions[permission.id]);
            permissions.forEach((permission) => {
                this.selectedAvailablePermissions[permission.id] = !allSelected;
            });
        },
        setAvailableCategoryCheckboxState(element, permissions) {
            if (!element) {
                return;
            }
            const selectedCount = permissions.filter((permission) => !!this.selectedAvailablePermissions[permission.id]).length;
            element.indeterminate = selectedCount > 0 && selectedCount < permissions.length;
        },
        isAssignedPermissionCategorySelected(permissions) {
            return permissions.length > 0 &&
                permissions.every((permission) => !!this.selectedAssignedPermissions[permission.id]);
        },
        toggleAssignedPermissionCategorySelection(permissions) {
            const allSelected = permissions.every((permission) => !!this.selectedAssignedPermissions[permission.id]);
            permissions.forEach((permission) => {
                this.selectedAssignedPermissions[permission.id] = !allSelected;
            });
        },
        setAssignedCategoryCheckboxState(element, permissions) {
            if (!element) {
                return;
            }
            const selectedCount = permissions.filter((permission) => !!this.selectedAssignedPermissions[permission.id]).length;
            element.indeterminate = selectedCount > 0 && selectedCount < permissions.length;
        },
        isAvailablePermissionSelected(permissionId) {
            return !!this.selectedAvailablePermissions[permissionId];
        },
        isAssignedPermissionSelected(permissionId) {
            return !!this.selectedAssignedPermissions[permissionId];
        },
        toggleAvailablePermission(permission) {
            const id = permission.id;
            this.selectedAvailablePermissions[id] = !this.selectedAvailablePermissions[id];
        },
        toggleAssignedPermission(permission) {
            const id = permission.id;
            this.selectedAssignedPermissions[id] = !this.selectedAssignedPermissions[id];
        },

        /* ---------------- Permission picker: moving between the lists ---------------- */
        moveSelectedPermissionsToAssigned() {
            const selectedIds = Object.keys(this.selectedAvailablePermissions)
                .filter((id) => this.selectedAvailablePermissions[id])
                .map(Number);

            if (selectedIds.length === 0) {
                return;
            }

            const selectedPermissions = this.availablePermissions.filter((permission) =>
                selectedIds.includes(Number(permission.id))
            );

            let receivingCategoryName = null;

            selectedPermissions.forEach((permission) => {
                const exists = this.assignedPermissions.some((item) => Number(item.id) === Number(permission.id));
                if (!exists) {
                    this.assignedPermissions.push(permission);
                }
                receivingCategoryName = permission.permission_cat_name || "Other";
            });

            this.availablePermissions = this.availablePermissions.filter((permission) =>
                !selectedIds.includes(Number(permission.id))
            );

            this.formInputs.permissions = [...this.assignedPermissions];

            this.selectedAvailablePermissions = {};
            this.selectedAssignedPermissions = {};

            this.expandedAssignedPermissionCategories = [];
            if (receivingCategoryName !== null) {
                this.expandedAssignedPermissionCategories = [receivingCategoryName];
            }
        },
        moveSelectedPermissionsToAvailable() {
            const selectedIds = Object.keys(this.selectedAssignedPermissions)
                .filter((id) => this.selectedAssignedPermissions[id])
                .map(Number);

            if (selectedIds.length === 0) {
                return;
            }

            const selectedPermissions = this.assignedPermissions.filter((permission) =>
                selectedIds.includes(Number(permission.id))
            );

            let receivingCategoryName = null;

            selectedPermissions.forEach((permission) => {
                const exists = this.availablePermissions.some((item) => Number(item.id) === Number(permission.id));
                if (!exists) {
                    this.availablePermissions.push(permission);
                }
                receivingCategoryName = permission.permission_cat_name || "Other";
            });

            this.assignedPermissions = this.assignedPermissions.filter((permission) =>
                !selectedIds.includes(Number(permission.id))
            );

            this.formInputs.permissions = [...this.assignedPermissions];

            this.selectedAvailablePermissions = {};
            this.selectedAssignedPermissions = {};

            this.expandedAvailablePermissionCategories = [];
            if (receivingCategoryName !== null) {
                this.expandedAvailablePermissionCategories = [receivingCategoryName];
            }
        },

        /* ---------------- Permission picker: open / close categories ---------------- */
        isAvailablePermissionCategoryExpanded(categoryName) {
            return this.expandedAvailablePermissionCategories.includes(categoryName);
        },
        toggleAvailablePermissionCategory(categoryName) {
            const index = this.expandedAvailablePermissionCategories.indexOf(categoryName);
            this.expandedAvailablePermissionCategories = index === -1 ? [categoryName] : [];
        },
        isAssignedPermissionCategoryExpanded(categoryName) {
            return this.expandedAssignedPermissionCategories.includes(categoryName);
        },
        toggleAssignedPermissionCategory(categoryName) {
            const index = this.expandedAssignedPermissionCategories.indexOf(categoryName);
            this.expandedAssignedPermissionCategories = index === -1 ? [categoryName] : [];
        },
        // Picker lists grouped by category, categories and names in A-Z order
        groupPermissionsByCategory(permissions) {
            const groups = {};

            permissions.forEach((permission) => {
                const categoryName = permission.permission_cat_name || "Other";
                if (!groups[categoryName]) {
                    groups[categoryName] = [];
                }
                groups[categoryName].push(permission);
            });

            return Object.keys(groups)
                .sort((a, b) => a.localeCompare(b))
                .reduce((sortedGroups, categoryName) => {
                    sortedGroups[categoryName] = groups[categoryName].sort((a, b) => a.name.localeCompare(b.name));
                    return sortedGroups;
                }, {});
        },

        /* ---------------- Form ---------------- */
        // Add button: empty form, every permission back on the "available" side
        clearForm() {
            this.isEditing = false;
            this.isSaving = false;
            this.submitted = false;
            this.formInputs = this.getDefaultFormInputs(); // every field, incl. USD price, trial, switches
            this.resetPermissionPicker();
            this.$clearValidationStates("#recordForm");
        },
        // Permissions loaded: fill the picker (all start on the "available" side)
        handleFetchPermissionsSuccess(response) {
            this.permissionOptions = (response.data.results || [])
                .filter((option) => option && option.id !== undefined && option.permission)
                .map((option) => ({
                    ...option,
                    name: option.permission,
                }));

            this.assignedPermissions = [];
            this.availablePermissions = [...this.permissionOptions];

            this.selectedAvailablePermissions = {};
            this.selectedAssignedPermissions = {};

            this.expandedAvailablePermissionCategories = [];
            this.expandedAssignedPermissionCategories = [];
        },

        /* ---------------- Delete ---------------- */
        // Delete one plan: explains why a plan in use can't be deleted
        deleteRecord(id) {
            this.$showOrHideLoader(this.tableData.loaderMessage);
            const data = { id, deleteType: this.tableData.deleteType, action: "deleteRecord" };

            axios
                .delete(this.apiUrl, this.$handleConfig(data))
                .then((response) => {
                    if (response.data.status == "success") {
                        this.handleDeleteRecordSuccess(response);
                    } else {
                        this.tableData.isVisible = false;
                        this.handleError("ending", "This plan cannot be deleted. Companies are using it, or it is the sign-up plan. To retire it, untick \"Show to users\".", "danger", "page");
                    }
                })
                .catch((error) => this.handleRequestError(error, "page"));
        },
        // "Delete Selected": some plans may be refused (in use / sign-up plan)
        deleteSelectedRecords() {
            const ids = this.tableData.selectedRecordIds;
            if (!ids.length) {
                this.handleAlert("ending", "No records selected.", "info", "page");
                return;
            }
            if (!confirm(`Are you sure you want to delete ${ids.length} record(s)?`)) return;

            this.$showOrHideLoader(this.$loaderMessages.deleting);
            const data = { action: "deleteRecord", ids, deleteType: "soft" };

            axios
                .delete(this.apiUrl, this.$handleConfig(data))
                .then((response) => {
                    if (response.data.status === "success") {
                        this.handleAlert("ending", this.$loaderMessages.deletingSelectedSuccess, "success", "page");
                        this.tableData.selectedRecordIds = [];
                        this.tableData.selectAll = false;
                    } else {
                        this.handleError("ending", "Some plans were not deleted. Companies are using them, or one is the sign-up plan.", "danger", "page");
                    }
                    this.fetchRecords();
                })
                .catch((error) => this.handleRequestError(error, "page"));
        },
    },
};